import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/services/LoggingService";

export async function fetchStockFundamentals(searchQuery: string) {
  logger.debug('AssetSearchService', 'Fetching stock fundamentals', { searchQuery });
  try {
    const searchTerm = searchQuery?.trim() || '';
    let query: any;

    if ((searchTerm?.length || 0) > 0) {
      query = supabase
        .from('stock_fundamentals')
        .select('*')
        .or(`code.ilike.${searchTerm}%,name.ilike.%${searchTerm}%`)
        .limit(50);
    } else {
      query = supabase
        .from('stock_fundamentals')
        .select('*')
        .order('code', { ascending: true })
        .limit(20);
    }

    const { data, error } = await query;
    if (error) {
      logger.error('AssetSearchService', 'Error fetching stock fundamentals', { error });
      return [];
    }
    logger.info('AssetSearchService', 'Stock fundamentals fetch completed', { count: data?.length || 0 });
    return data || [];
  } catch (err) {
    logger.error('AssetSearchService', 'Exception in stock fundamentals query', { err });
    return [];
  }
}

export async function fetchFundsRaw(assetClass: string, searchQuery: string) {
  logger.debug('AssetSearchService', 'Fetching funds_raw', { assetClass, searchQuery });
  try {
    let baseQuery = supabase.from('funds_raw').select('*');

    if (assetClass) {
      switch (assetClass) {
        case 'equity':
          baseQuery = baseQuery.eq('sa_asset_class', 'equity');
          break;
        case 'fixed_income':
          baseQuery = baseQuery.or('sa_asset_class.eq.fixed income,sa_asset_class.eq.fixed_income');
          break;
        case 'cash':
          baseQuery = baseQuery.or('sa_asset_class.eq.cash,sa_asset_class.eq.cash_equivalent');
          break;
        case 'alternative':
          baseQuery = baseQuery.eq('sa_asset_class', 'alternative');
          break;
        case 'multi_asset':
          baseQuery = baseQuery.eq('sa_asset_class', 'unclassified');
          break;
        default:
          // no-op
          break;
      }
    }

    if (searchQuery && searchQuery.trim()) {
      const searchTerm = searchQuery.trim();
      if (searchTerm.includes(' ')) {
        const parts = searchTerm.split(' ').filter((p) => (p?.length || 0) > 0);
        if ((parts?.length || 0) === 2) {
          const [firstPart, secondPart] = parts;
          baseQuery = baseQuery
            .or(`management_company_code.ilike.%${firstPart}%,fund_name.ilike.%${searchTerm}%`)
            .or(`fund_code.ilike.%${secondPart}%,fund_name.ilike.%${searchTerm}%`);
        } else {
          baseQuery = baseQuery.or(`fund_name.ilike.%${searchTerm}%`);
        }
      } else {
        baseQuery = baseQuery.or(
          `fund_name.ilike.%${searchTerm}%,fund_code.ilike.%${searchTerm}%,management_company_code.ilike.%${searchTerm}%`
        );
      }
    }

    baseQuery = baseQuery.limit(50);
    const { data, error } = await baseQuery;
    if (error) {
      logger.error('AssetSearchService', 'Error fetching funds_raw', { error });
      return [];
    }
    logger.info('AssetSearchService', 'Funds_raw fetch completed', { count: data?.length || 0 });
    return data || [];
  } catch (err) {
    logger.error('AssetSearchService', 'Exception in funds_raw query', { err });
    return [];
  }
}

export async function fetchFunds(assetClass: string, searchQuery: string) {
  logger.debug('AssetSearchService', 'Fetching funds', { assetClass, searchQuery });
  try {
    let baseQuery = supabase.from('funds').select('*').eq('is_approved', true);

    if (assetClass) {
      switch (assetClass) {
        case 'equity':
          baseQuery = baseQuery.eq('asset_class', 'equity');
          break;
        case 'fixed_income':
          baseQuery = baseQuery.eq('asset_class', 'fixed_income');
          break;
        case 'cash':
          baseQuery = baseQuery.eq('asset_class', 'cash');
          break;
        case 'alternative':
          baseQuery = baseQuery.eq('asset_class', 'alternative');
          break;
        case 'multi_asset':
          baseQuery = baseQuery.or('asset_class.eq.multi_asset,asset_class.is.null');
          break;
        default:
          break;
      }
    }

    if (searchQuery && searchQuery.trim()) {
      const searchTerm = searchQuery.trim();
      baseQuery = baseQuery.or(
        `fund_name.ilike.%${searchTerm}%,fund_code.ilike.%${searchTerm}%,investment_strategy.ilike.%${searchTerm}%`
      );
    }

    baseQuery = baseQuery.limit(50);
    const { data, error } = await baseQuery;
    if (error) {
      logger.error('AssetSearchService', 'Error fetching funds', { error });
      return [];
    }
    logger.info('AssetSearchService', 'Funds fetch completed', { count: data?.length || 0 });
    return data || [];
  } catch (err) {
    logger.error('AssetSearchService', 'Exception in funds query', { err });
    return [];
  }
}
